import React, { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useSocketState } from '../../helpers/useSocket'
import PlayerPicker from '../../components/PlayerPicker'
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FaExternalLinkAlt } from 'react-icons/fa'

const generateId = () => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID()
	}
	return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) => (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16))
}

const emptyEntry = () => ({
	id: generateId(),
	matchTitle: '',
	startTime: '',
	startTimeOfDay: '',
	matchTypeDisplay: '',
	mapDraftId: '',
	civDraftId: '',
	leftPlayer: { name: '', score: 0, country: '' },
	rightPlayer: { name: '', score: 0, country: '' },
	finished: false,
})

const emptyDay = (index) => ({
	label: `Day ${index + 1}`,
	date: '',
	matches: [],
})

export function DragHandle({ listeners, attributes }) {
	return (
		<button className="cursor-grab active:cursor-grabbing px-1 text-gray-400 hover:text-gray-200 touch-none" {...listeners} {...attributes}>
			<svg width="16" height="24" viewBox="0 0 16 24" fill="currentColor">
				<circle cx="4" cy="4" r="2" />
				<circle cx="12" cy="4" r="2" />
				<circle cx="4" cy="12" r="2" />
				<circle cx="12" cy="12" r="2" />
				<circle cx="4" cy="20" r="2" />
				<circle cx="12" cy="20" r="2" />
			</svg>
		</button>
	)
}

function SortableMatch({ id, entry, index, matchNumber, tournament, updateEntry, updatePlayer, removeEntry, loadMatch }) {
	const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id })

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
	}

	return (
		<div ref={setNodeRef} style={style} className="relative flex flex-row gap-4 p-3 border rounded border-gray-600">
			<span className="absolute top-1 left-2 text-xs text-gray-600">Match {matchNumber}</span>

			<div className="self-center">
				<DragHandle listeners={listeners} attributes={attributes} />
			</div>

			<div className="flex flex-col">
				<div className="flex flex-row mb-2">
					<div className="flex flex-col">
						<label>Match Title</label>
						<input
							type="text"
							className="w-48"
							value={entry.matchTitle}
							onChange={(e) => updateEntry(index, { matchTitle: e.target.value })}
							placeholder="e.g. Quarter Final 1"
						/>
					</div>

					<div className="flex flex-col">
						<label>Date</label>
						<input type="date" className="w-40" value={entry.startTime} onChange={(e) => updateEntry(index, { startTime: e.target.value })} />
					</div>

					<div className="flex flex-col">
						<label>Time</label>
						<input type="time" className="w-28" value={entry.startTimeOfDay} onChange={(e) => updateEntry(index, { startTimeOfDay: e.target.value })} />
					</div>

					<div className="flex flex-col items-center">
						<label>Finished</label>
						<input type="checkbox" checked={entry.finished} onChange={(e) => updateEntry(index, { finished: e.target.checked })} className="w-5 h-5 mt-2" />
					</div>

					<button className="bg-blue-600 hover:bg-blue-800 h-8" onClick={() => loadMatch(entry)}>
						Load Match
					</button>

					<button className="bg-red-600 hover:bg-red-800 h-8" onClick={() => removeEntry(index)}>
						Remove
					</button>
				</div>

				<div className="flex flex-row">
					<div className="flex flex-col">
						<label>Left Player</label>
						<PlayerPicker
							players={tournament?.config?.players}
							value={entry.leftPlayer.name}
							onChange={(name) => updatePlayer(index, 'leftPlayer', { name })}
							countryValue={entry.leftPlayer.country?.toUpperCase()}
							countryOnChange={(country) => updatePlayer(index, 'leftPlayer', { country })}
						/>
					</div>

					<div className="flex flex-col w-16">
						<label>L Score</label>
						<input type="number" min="0" value={entry.leftPlayer.score} onChange={(e) => updatePlayer(index, 'leftPlayer', { score: Number(e.target.value) })} />
					</div>

					<div className="flex flex-col">
						<label>Best Of</label>
						<input
							type="text"
							value={entry.matchTypeDisplay}
							onChange={(e) => updateEntry(index, { matchTypeDisplay: e.target.value })}
							placeholder="e.g. Bo5"
							className="w-20"
						/>
					</div>

					<div className="flex flex-col w-16">
						<label>R Score</label>
						<input type="number" min="0" value={entry.rightPlayer.score} onChange={(e) => updatePlayer(index, 'rightPlayer', { score: Number(e.target.value) })} />
					</div>

					<div className="flex flex-col">
						<label>Right Player</label>
						<PlayerPicker
							players={tournament?.config?.players}
							value={entry.rightPlayer.name}
							onChange={(name) => updatePlayer(index, 'rightPlayer', { name })}
							countryValue={entry.rightPlayer.country?.toUpperCase()}
							countryOnChange={(country) => updatePlayer(index, 'rightPlayer', { country })}
						/>
					</div>
				</div>

				<div className="flex flex-row gap-4">
					<div className="flex flex-col">
						<label>Map Draft</label>
						<div className="flex flex-row items-center gap-1">
							<input
								type="text"
								className="w-32"
								value={entry.mapDraftId}
								onChange={(e) => updateEntry(index, { mapDraftId: e.target.value })}
								placeholder="MapDraft"
							/>
							<a
								href={`https://aoe2cm.net/draft/${entry.mapDraftId}`}
								target="_blank"
								rel="noreferrer"
								className={`h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${entry.mapDraftId ? '' : 'pointer-events-none opacity-40'}`}
								title="Open map draft in new tab">
								<FaExternalLinkAlt />
							</a>
						</div>
					</div>

					<div className="flex flex-col">
						<label>Civ Draft</label>
						<div className="flex flex-row items-center gap-1">
							<input
								type="text"
								className="w-32"
								value={entry.civDraftId}
								onChange={(e) => updateEntry(index, { civDraftId: e.target.value })}
								placeholder="CivDraft"
							/>
							<a
								href={`https://aoe2cm.net/draft/${entry.civDraftId}`}
								target="_blank"
								rel="noreferrer"
								className={`h-8 flex items-center px-2 rounded bg-teal-700 hover:bg-teal-800 text-white ${entry.civDraftId ? '' : 'pointer-events-none opacity-40'}`}
								title="Open civ draft in new tab">
								<FaExternalLinkAlt />
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function ScheduleDashboard() {
	const { socket } = useOutletContext()
	const [tournament] = useSocketState('Tournament')
	const [activeDay, setActiveDay] = useState(0)
	const [timezone, setTimezone] = useState('UTC')
	const [days, setDays] = useState([])

	const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

	useEffect(() => {
		if (tournament?.schedule) {
			const s = tournament.schedule
			setActiveDay(s.activeDay ?? 0)
			setTimezone(s.timezone ?? 'UTC')
			setDays(
				(s.days ?? []).map((day) => ({
					label: day.label ?? '',
					date: day.date ?? '',
					matches: (day.matches ?? []).map((m) => ({
						id: m.id || generateId(),
						matchTitle: m.matchTitle ?? '',
						startTime: m.startTime ?? '',
						startTimeOfDay: m.startTimeOfDay ?? '',
						matchTypeDisplay: m.matchTypeDisplay ?? '',
						mapDraftId: m.mapDraftId ?? '',
						civDraftId: m.civDraftId ?? '',
						leftPlayer: {
							name: m.leftPlayer?.name ?? '',
							score: m.leftPlayer?.score ?? 0,
							country: m.leftPlayer?.country ?? '',
						},
						rightPlayer: {
							name: m.rightPlayer?.name ?? '',
							score: m.rightPlayer?.score ?? 0,
							country: m.rightPlayer?.country ?? '',
						},
						finished: m.finished ?? false,
					})),
				})),
			)
		}
	}, [tournament?.schedule])

	const currentDay = days[activeDay]

	function updateDayField(field, value) {
		setDays((prev) => prev.map((d, i) => (i === activeDay ? { ...d, [field]: value } : d)))
	}

	function updateEntry(index, patch) {
		setDays((prev) => prev.map((d, i) => (i === activeDay ? { ...d, matches: d.matches.map((m, j) => (j === index ? { ...m, ...patch } : m)) } : d)))
	}

	function updatePlayer(index, side, patch) {
		setDays((prev) =>
			prev.map((d, i) =>
				i === activeDay
					? {
							...d,
							matches: d.matches.map((m, j) => (j === index ? { ...m, [side]: { ...m[side], ...patch } } : m)),
						}
					: d,
			),
		)
	}

	function addEntry() {
		setDays((prev) => prev.map((d, i) => (i === activeDay ? { ...d, matches: [...d.matches, emptyEntry()] } : d)))
	}

	function removeEntry(index) {
		setDays((prev) => prev.map((d, i) => (i === activeDay ? { ...d, matches: d.matches.filter((_, j) => j !== index) } : d)))
	}

	function addDay() {
		setDays((prev) => [...prev, emptyDay(prev.length)])
	}

	function duplicateDay(index) {
		const source = days[index]
		if (!source) return
		const copy = {
			...source,
			label: `${source.label || `Day ${index + 1}`} (copy)`,
			// Only the match shell carries over; players, scores, drafts and dates start blank.
			matches: source.matches.map((m) => ({
				...emptyEntry(),
				matchTitle: m.matchTitle,
				startTimeOfDay: m.startTimeOfDay,
				matchTypeDisplay: m.matchTypeDisplay,
			})),
		}
		setDays((prev) => [...prev.slice(0, index + 1), copy, ...prev.slice(index + 1)])
		setActiveDay(index + 1)
	}

	function removeDay(index) {
		if (days.length <= 1) return
		if (!window.confirm(`Remove "${days[index].label || `Day ${index + 1}`}" and all its matches?`)) return
		setDays((prev) => prev.filter((_, i) => i !== index))
		if (activeDay >= index && activeDay > 0) {
			setActiveDay((prev) => prev - 1)
		}
	}

	function handleDragEnd(event) {
		const { active, over } = event
		if (!over || active.id === over.id) return

		setDays((prev) =>
			prev.map((d, i) => {
				if (i !== activeDay) return d
				const oldIndex = d.matches.findIndex((m) => m.id === active.id)
				const newIndex = d.matches.findIndex((m) => m.id === over.id)
				return { ...d, matches: arrayMove(d.matches, oldIndex, newIndex) }
			}),
		)
	}

	function saveSchedule() {
		socket.emit('updateSchedule', { activeDay, timezone, days })
	}

	function setActiveDayAndSave(index) {
		setActiveDay(index)
		socket.emit('updateSchedule', { activeDay: index, timezone, days })
	}

	function loadMatch(entry) {
		const summary =
			`Load this match into the live match?\n\n` +
			`${entry.matchTitle || '(no title)'}\n` +
			`${entry.leftPlayer.name || '(empty)'} vs ${entry.rightPlayer.name || '(empty)'}\n` +
			`Score: ${entry.leftPlayer.score} - ${entry.rightPlayer.score}\n` +
			`Map Draft: ${entry.mapDraftId || '—'}\n` +
			`Civ Draft: ${entry.civDraftId || '—'}`
		if (!window.confirm(summary)) return

		socket.emit('modifyLeftPlayer', {
			name: entry.leftPlayer.name,
			country: entry.leftPlayer.country,
			score: entry.leftPlayer.score,
		})
		socket.emit('modifyRightPlayer', {
			name: entry.rightPlayer.name,
			country: entry.rightPlayer.country,
			score: entry.rightPlayer.score,
		})
		socket.emit('modifyMatch', { matchTitle: entry.matchTitle ?? '' })
		if (entry.mapDraftId) socket.emit('setMapDraftId', entry.mapDraftId)
		if (entry.civDraftId) socket.emit('setCivDraftId', entry.civDraftId)
	}

	return (
		<div className="mt-4">
			<div className="flex flex-row items-center gap-4 mb-4">
				<h2 className="text-lg font-bold">Schedule</h2>
				<button className="" onClick={addDay}>
					Add Day
				</button>
				<button className="" onClick={addEntry} disabled={!currentDay}>
					Add Match
				</button>
				<div className="flex flex-col">
					<label className="text-xs">Timezone</label>
					<input type="text" value={timezone} onChange={(e) => setTimezone(e.target.value)} placeholder="e.g. CET" className="w-24" />
				</div>
				<button className="bg-green-600 hover:bg-green-700" onClick={saveSchedule}>
					Save Schedule
				</button>
			</div>

			{/* Day tabs */}
			<div className="flex flex-row gap-1 mb-4">
				{days.map((day, i) => (
					<div key={i} className="flex flex-row items-center">
						<button className={`rounded-b-none ${i === activeDay ? 'bg-blue-600' : ''}`} onClick={() => setActiveDay(i)}>
							{day.label || `Day ${i + 1}`}
						</button>
						{i === activeDay && <span className="text-xs text-green-400 ml-1">ACTIVE</span>}
						<button
							className="rounded-b-none bg-transparent text-xs px-1 text-green-400 hover:text-green-300"
							onClick={() => setActiveDayAndSave(i)}
							title="Set as vmix output day">
							{tournament?.schedule?.activeDay === i ? '★' : '☆'}
						</button>
					</div>
				))}
			</div>

			{/* Day settings */}
			{currentDay && (
				<div className="flex flex-row items-end gap-4 mb-4">
					<div className="flex flex-col">
						<label>Day Label</label>
						<input type="text" className="w-48" value={currentDay.label} onChange={(e) => updateDayField('label', e.target.value)} placeholder="e.g. Day 1" />
					</div>
					<div className="flex flex-col">
						<label>Date</label>
						<input type="date" className="w-48" value={currentDay.date} onChange={(e) => updateDayField('date', e.target.value)} />
					</div>
					<button className="bg-purple-600 hover:bg-purple-800 h-8" onClick={() => duplicateDay(activeDay)}>
						Duplicate Day
					</button>
					<button className="bg-red-600 hover:bg-red-800 h-8" onClick={() => removeDay(activeDay)} disabled={days.length <= 1}>
						Remove Day
					</button>
				</div>
			)}

			{/* Matches */}
			{currentDay && (
				<>
					<p className="text-sm text-gray-400 mb-2">
						{currentDay.matches.length} match{currentDay.matches.length !== 1 ? 'es' : ''}
					</p>
					<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
						<SortableContext items={currentDay.matches.map((m) => m.id)} strategy={verticalListSortingStrategy}>
							<div className="flex flex-col gap-4">
								{currentDay.matches.map((entry, i) => (
									<SortableMatch
										key={entry.id}
										id={entry.id}
										entry={entry}
										index={i}
										matchNumber={i + 1}
										tournament={tournament}
										updateEntry={updateEntry}
										updatePlayer={updatePlayer}
										removeEntry={removeEntry}
										loadMatch={loadMatch}
									/>
								))}
							</div>
						</SortableContext>
					</DndContext>
				</>
			)}

			{days.length === 0 && <p className="text-gray-400">No days added. Click "Add Day" to start.</p>}
		</div>
	)
}

export default ScheduleDashboard
